import { browser } from '$app/environment';
import { v4 as uuidv4 } from 'uuid';
import { COOKIES, LOCAL_STORAGE_KEYS, QUERY_PARAM_KEYS } from './constants';
import * as Sentry from '@sentry/sveltekit';
import Cookies from 'js-cookie';
import { get } from 'svelte/store';
import { app } from '$lib/stores/app/store';
import { AnalyticsService } from '$lib/services/analytics/index.js';


let deviceIdFallbackUsed = false;

// Generic localStorage store abstraction, accessible at the top level.
// Uses in-memory object if localStorage is unavailable.
/** @type {Record<string, string>} */
const inMemoryStorage = {};
export const localStorageStore = (() => {
	return {
		/**
		 * @param {string} key
		 * @param {string} value
		 */
		set: function (key, value) {
			try {
				localStorage.setItem(key, value);
			} catch (e) {
				inMemoryStorage[key] = value;
			}
		},
		/**
		 * @param {string} key
		 * @returns {string | null}
		 */
		get: function (key) {
			try {
				return localStorage.getItem(key);
			} catch (e) {
				return inMemoryStorage.hasOwnProperty(key) ? inMemoryStorage[key] : null;
			}
		}
	};
})();

/**
 * Gets an existing device ID from cookies.
 * Supports both browser (uses js-cookie) and SSR (uses cookies context).
 * @param {object} [context] - Optional context object
 * @param {import('@sveltejs/kit').Cookies} [context.cookies] - SvelteKit cookies object for SSR (from load function)
 * @returns {string | null} The existing device ID,
 */
export const getDeviceId = (context) => {
	let determinedDeviceId;
	if (typeof window !== 'undefined') {
		const browserCookiedDeviceId = Cookies.get(COOKIES.DEVICE_ID);
		const localStorageDeviceIdBackUp = localStorageStore.get(LOCAL_STORAGE_KEYS.DEVICE_ID_BACKUP);
		// TODO remove this after the 'null' deviceID discrepancy is resolved.
		// in a prior release, null was erroneously assigned to the deviceID cookie.
		if (browserCookiedDeviceId === 'null') {
			createAndSaveDeviceId();
			determinedDeviceId =
				Cookies.get(COOKIES.DEVICE_ID) ??
				localStorageStore.get(LOCAL_STORAGE_KEYS.DEVICE_ID_BACKUP);
		} else if (browserCookiedDeviceId) {
			determinedDeviceId = browserCookiedDeviceId;
		} else if (localStorageDeviceIdBackUp) {
			// TMP FIX: fallbacks to meta device_id when cookie isn't available
			determinedDeviceId = localStorageDeviceIdBackUp;

			if (!deviceIdFallbackUsed) {
				AnalyticsService.statelessEventLog(
					{
						event_subtype: 'device_id_fallback',
						browser,
						window: typeof window !== 'undefined'
					},
					localStorageDeviceIdBackUp
				).catch((err) =>
					Sentry.captureException(err, {
						extra: {
							action: 'getDeviceId fallback error'
						}
					})
				);

				deviceIdFallbackUsed = true;
			}
		} else {
			// This is for prior releases where the device ID was stored in local storage.
			const existingDeviceIdFromLocalStorage = migrateDeviceIdFromLocalStorage();
			determinedDeviceId = existingDeviceIdFromLocalStorage;
		}
	}

	if (context?.cookies) {
		const serverCookiedDeviceId = context.cookies.get(COOKIES.DEVICE_ID);
		// TODO remove this after the 'null' deviceID discrepancy is resolved.
		// in a prior release, null was erroneously assigned to the deviceID cookie.
		if (serverCookiedDeviceId === 'null') {
			createAndSaveDeviceId(context);
			determinedDeviceId = context.cookies.get(COOKIES.DEVICE_ID);
		}
		if (serverCookiedDeviceId) determinedDeviceId = serverCookiedDeviceId;
	}

	return determinedDeviceId ?? null;
};

/**
 * Gets an existing device ID from local storage.
 * Device ID in local storage is deprecated but this ensures we transfer existing device IDs to cookies.
 * @returns {string | null} The existing device ID from local storage, or null if none exists
 */
const migrateDeviceIdFromLocalStorage = () => {
	if (!browser) return null;
	const existingDeviceIdFromLocalStorage = localStorage.getItem(LOCAL_STORAGE_KEYS.DEVICE_ID);
	if (existingDeviceIdFromLocalStorage) {
		if (existingDeviceIdFromLocalStorage === 'null') {
			createAndSaveDeviceId();
			localStorage.removeItem(LOCAL_STORAGE_KEYS.DEVICE_ID);
			return null;
		} else {
			saveDeviceId(existingDeviceIdFromLocalStorage);
			localStorage.removeItem(LOCAL_STORAGE_KEYS.DEVICE_ID);
			return existingDeviceIdFromLocalStorage;
		}
	}
	return null;
};

/**
 * Sets Sentry tags and user context for a device ID.
 * This should be called after the device ID is saved to cookies.
 * @param {string} deviceId - The device ID to set in Sentry
 */
const setSentryDeviceId = (deviceId) => {
	Sentry.setTag(COOKIES.DEVICE_ID, deviceId);
	Sentry.setUser({ id: deviceId });
};

/**
 * Creates a new device ID and saves it to cookies.
 * Also sets Sentry tags and user context.
 * @param {object} [context] - Optional context object for SSR
 * @returns {void} The newly created device ID
 */
const createAndSaveDeviceId = (context) => {
	const deviceId = uuidv4();
	saveDeviceId(deviceId, context);
	return;
};

/**
 * Saves a device ID to cookies.
 * Supports both browser (uses js-cookie) and SSR (uses cookies context).
 * Also sets Sentry tags and user context after saving.
 * @param {string} deviceId - The device ID to save
 * @param {object} [context] - Optional context object
 * @param {import('@sveltejs/kit').Cookies} [context.cookies] - SvelteKit cookies object for SSR
 */
export const saveDeviceId = (deviceId, context) => {
	if (browser) {
		Cookies.set(COOKIES.DEVICE_ID, deviceId, COOKIES.DEVICE_ID_CONFIG);
	} else if (context?.cookies) {
		context.cookies.set(COOKIES.DEVICE_ID, deviceId, COOKIES.DEVICE_ID_SSR_CONFIG);
	}

	// Set backup when saving device ID
	localStorageStore.set(LOCAL_STORAGE_KEYS.DEVICE_ID_BACKUP, deviceId);
	setSentryDeviceId(deviceId);
};

/**
 * Initializes the device ID once per web app load.
 * Works in both browser and SSR contexts.
 * This should be called early in the app lifecycle (e.g., in +layout.js).
 * Behavior:
 * - Checks URL params first (if found, saves it to cookies)
 * - Checks cookies for existing device ID
 * - Creates and saves a new device ID if none exists
 * Use getDeviceId() to read the device ID after initialization.
 * @param {object} [context] - Optional context object
 * @param {import('@sveltejs/kit').Cookies} [context.cookies] - SvelteKit cookies object for SSR (from load function)
 * @param {URL} [context.url] - URL object for SSR (from load function)
 * @returns {void} The device ID, or null if not initialized (SSR without context)
 */
export const initializeDeviceId = (context) => {
	if (browser) {
		const existingDeviceId = getDeviceId();
		const searchParams = new URLSearchParams(window.location.search);
		if (searchParams.has(QUERY_PARAM_KEYS.DEVICE_ID)) {
			const deviceIdFromUrl = searchParams.get(QUERY_PARAM_KEYS.DEVICE_ID) || '';
			if (deviceIdFromUrl) {
				AnalyticsService.statelessEventLog(
					{
						event_subtype: 'device_id_update',
						source: 'url_param',
						initial_device_id: existingDeviceId,
						new_device_id: deviceIdFromUrl
					},
					deviceIdFromUrl,
					false
				);
				saveDeviceId(deviceIdFromUrl);
				return;
			}
		}

		if (existingDeviceId) {
			setSentryDeviceId(existingDeviceId);
			return;
		}

		createAndSaveDeviceId();
		return;
	}

	if (context?.cookies) {
		if (context?.url) {
			try {
				const searchParams = context.url.searchParams;
				if (searchParams.has(QUERY_PARAM_KEYS.DEVICE_ID)) {
					const deviceIdFromUrl = searchParams.get(QUERY_PARAM_KEYS.DEVICE_ID) || '';
					if (deviceIdFromUrl) {
						saveDeviceId(deviceIdFromUrl, context);
						return;
					}
				}
			} catch (error) {
				console.error(error);
			}
		}

		const existingDeviceId = getDeviceId(context);
		if (existingDeviceId) {
			setSentryDeviceId(existingDeviceId);
			return;
		}

		createAndSaveDeviceId(context);
		return;
	}

	return;
};
